<!-- src/components/AppDialogModal.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import Modal from 'bootstrap/js/dist/modal'

const modalRef = ref(null)
let modalInstance = null
const isVisible = ref(false)

// Stan modala
const type = ref('alert') // 'alert' lub 'confirm'
const title = ref('')
const message = ref('')
const icon = ref('bi-info-circle')
const confirmText = ref('OK')
const cancelText = ref('Anuluj')
const confirmClass = ref('btn-primary')
const duration = ref(5000)

let autoCloseTimer = null
let resolvePromise = null

// Wyciąganie koloru z klasy ikony (np. text-success, text-danger)
const progressBarClass = computed(() => {
    const match = icon.value.match(/text-([a-z-]+)/)
    return match ? `bg-${match[1]}` : 'bg-primary'
})

const clearAutoClose = () => {
    if (autoCloseTimer) {
        clearTimeout(autoCloseTimer)
        autoCloseTimer = null
    }
}

const show = (options) => {
    clearAutoClose()

    type.value = options.type || 'alert'
    title.value = options.title || (type.value === 'confirm' ? 'Potwierdzenie' : 'Informacja')
    message.value = options.message || ''
    icon.value = options.icon || (type.value === 'confirm' ? 'bi-question-circle text-warning' : 'bi-info-circle text-primary')
    confirmText.value = options.confirmText || (type.value === 'confirm' ? 'Tak' : 'OK')
    cancelText.value = options.cancelText || 'Anuluj'
    confirmClass.value = options.confirmClass || (type.value === 'confirm' ? 'btn-danger' : 'btn-primary')
    duration.value = options.duration ?? 5000

    isVisible.value = true
    modalInstance.show()

    // Czasowe zamykanie TYLKO dla Alert (Confirm zostaje bez zmian)
    if (type.value === 'alert' && duration.value > 0) {
        autoCloseTimer = setTimeout(() => {
            handleConfirm()
        }, duration.value)
    }

    return new Promise((resolve) => {
        resolvePromise = resolve
    })
}

const handleConfirm = () => {
    clearAutoClose()
    isVisible.value = false
    modalInstance.hide()
    if (resolvePromise) resolvePromise(true)
}

const handleCancel = () => {
    clearAutoClose()
    isVisible.value = false
    modalInstance.hide()
    if (resolvePromise) resolvePromise(false)
}

const handleKeyDown = (event) => {
    if (!isVisible.value) return

    if (type.value === 'alert') {
        if (event.key === 'Enter' || event.key === 'Escape') {
            event.preventDefault()
            handleConfirm()
        }
    } else if (type.value === 'confirm') {
        if (event.key === 'Enter') {
            event.preventDefault()
            handleConfirm()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
        }
    }
}

onMounted(() => {
    modalInstance = new Modal(modalRef.value, { backdrop: 'static', keyboard: false })
    window.addEventListener('keydown', handleKeyDown)

    modalRef.value.addEventListener('hidden.bs.modal', () => {
        clearAutoClose()
        isVisible.value = false
    })
})

onUnmounted(() => {
    clearAutoClose()
    window.removeEventListener('keydown', handleKeyDown)
})

defineExpose({ show })
</script>

<template>
    <div ref="modalRef" class="modal fade" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
            <div class="modal-content border-0 shadow overflow-hidden">
                <!-- Pasek postępu (tylko dla alertów) -->
                <div v-if="type === 'alert' && isVisible && duration > 0" class="progress-bar-loader"
                    :class="progressBarClass"></div>

                <div class="modal-body text-center p-4">
                    <!-- Ikona -->
                    <div class="mb-3">
                        <i class="bi" :class="[icon, 'display-4']"></i>
                    </div>

                    <!-- Tytuł i treść -->
                    <h5 v-if="title" class="fw-bold mb-2">{{ title }}</h5>
                    <p class="text-muted small mb-4" v-html="message"></p>
                    <!-- Przyciski -->
                    <div class="d-flex justify-content-center gap-2">
                        <button v-if="type === 'confirm'" type="button" class="btn btn-light btn-sm px-3"
                            @click="handleCancel">
                            {{ cancelText }}
                        </button>
                        <button type="button" class="btn btn-sm px-4" :class="confirmClass" @click="handleConfirm">
                            {{ confirmText }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.progress-bar-loader {
    height: 4px;
    width: 100%;
    transform-origin: left;
    animation: shrinkBar v-bind('duration + "ms"') linear forwards;
}

@keyframes shrinkBar {
    from {
        transform: scaleX(1);
    }

    to {
        transform: scaleX(0);
    }
}
</style>